import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { FileText, Shield, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LegalModal = ({ isOpen, onClose, userType = 'patient', token, onSigned }) => {
  const [cguContent, setCguContent] = useState('');
  const [rgpdContent, setRgpdContent] = useState('');
  const [cguAccepted, setCguAccepted] = useState(false);
  const [rgpdAccepted, setRgpdAccepted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [currentTab, setCurrentTab] = useState('cgu');

  useEffect(() => {
    if (isOpen) {
      fetchDocuments();
    }
  }, [isOpen, userType]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const [cguRes, rgpdRes] = await Promise.all([
        axios.get(`${API_URL}/api/legal/cgu/${userType}`),
        axios.get(`${API_URL}/api/legal/rgpd`)
      ]);
      setCguContent(cguRes.data.content);
      setRgpdContent(rgpdRes.data.content);
    } catch (error) {
      toast.error('Erreur de chargement des documents');
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async () => {
    if (!cguAccepted || !rgpdAccepted) {
      toast.error('Veuillez accepter les deux documents');
      return;
    }

    setSigning(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Sign CGU
      await axios.post(`${API_URL}/api/legal/sign`, {
        document_type: userType === 'patient' ? 'cgu_patient' : 'cgu_professional'
      }, { headers });
      
      // Sign RGPD
      await axios.post(`${API_URL}/api/legal/sign`, {
        document_type: 'rgpd'
      }, { headers });
      
      toast.success('Documents signés avec succès');
      if (onSigned) onSigned();
      onClose();
    } catch (error) {
      toast.error('Erreur lors de la signature');
    } finally {
      setSigning(false);
    }
  };

  const renderMarkdown = (content) => {
    // Simple markdown rendering
    return content
      .split('\n')
      .map((line, idx) => {
        if (line.startsWith('# ')) {
          return <h1 key={idx} className="text-xl font-bold mb-4 text-blue-600">{line.slice(2)}</h1>;
        }
        if (line.startsWith('## ')) {
          return <h2 key={idx} className="text-lg font-semibold mt-4 mb-2">{line.slice(3)}</h2>;
        }
        if (line.startsWith('### ')) {
          return <h3 key={idx} className="font-semibold mt-3 mb-1">{line.slice(4)}</h3>;
        }
        if (line.startsWith('- ')) {
          return <li key={idx} className="ml-4">{line.slice(2)}</li>;
        }
        if (line.trim() === '') {
          return <br key={idx} />;
        }
        return <p key={idx} className="mb-2">{line}</p>;
      });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-blue-500" />
            Conditions d'utilisation
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Tabs */}
            <div className="flex border-b">
              <button
                className={`px-4 py-2 font-medium ${currentTab === 'cgu' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-slate-500'}`}
                onClick={() => setCurrentTab('cgu')}
              >
                <FileText className="w-4 h-4 inline mr-2" />
                CGU {userType === 'patient' ? 'Patient' : 'Professionnel'}
              </button>
              <button
                className={`px-4 py-2 font-medium ${currentTab === 'rgpd' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-slate-500'}`}
                onClick={() => setCurrentTab('rgpd')}
              >
                <Shield className="w-4 h-4 inline mr-2" />
                RGPD
              </button>
            </div>

            {/* Content */}
            <ScrollArea className="h-[400px] border rounded-lg p-4 bg-slate-50">
              <div className="text-sm">
                {currentTab === 'cgu' ? renderMarkdown(cguContent) : renderMarkdown(rgpdContent)}
              </div>
            </ScrollArea>

            {/* Checkboxes */}
            <div className="space-y-3 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="cgu"
                  checked={cguAccepted}
                  onCheckedChange={setCguAccepted}
                />
                <label htmlFor="cgu" className="text-sm cursor-pointer">
                  J'ai lu et j'accepte les <strong>Conditions Générales d'Utilisation</strong>
                </label>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="rgpd"
                  checked={rgpdAccepted}
                  onCheckedChange={setRgpdAccepted}
                />
                <label htmlFor="rgpd" className="text-sm cursor-pointer">
                  J'ai lu et j'accepte la <strong>Politique de Protection des Données (RGPD)</strong>
                </label>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button 
            onClick={handleSign} 
            disabled={!cguAccepted || !rgpdAccepted || signing}
            className="bg-green-600 hover:bg-green-700"
          >
            {signing ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                Signature...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Signer et accepter
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LegalModal;
